import React, { useState, useEffect } from "react";
import { Loader, Calendar } from "lucide-react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import apiService from "../../services/apiService";
import { RECON } from "../../services/apiConstant";
import { useAuth } from "../../providers/AuthProvider";
import type { Vehicle, ReconStage, ChangeLocationFormData } from "../../types/vehicle";
import type { VehicleStage } from "../../hooks/useVehicleProvider";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useAddVehicleNote } from "../../hooks/useVehicleData";
import { uploadManager } from "../../services/uploadManager";
import { FilePickerZone } from "../FilePickerZone";
import { toast } from "sonner";

interface ChangeLocationModalProps {
  vehicle: Vehicle;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  currentStage?: VehicleStage;
}

interface LocationOption {
  id: number;
  name: string;
  lot: string;
  stage: string;
  stage2: string;
}

interface StagesResponse {
  success: boolean;
  stages?: ReconStage[];
}

export function ChangeLocationModal({
  vehicle,
  open,
  onOpenChange,
  onSuccess,
  currentStage,
}: ChangeLocationModalProps) {
  const { user } = useAuth();
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [formData, setFormData] = useState<ChangeLocationFormData>({
    locationNew: null,
  });
  const [eta, setEta] = useState<string>("");
  const [initialLocationId, setInitialLocationId] = useState<number | null>(null);
  const [initialEta, setInitialEta] = useState<string>("");
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const queryClient = useQueryClient();
  const addNoteMutation = useAddVehicleNote();

  // Calculate default ETA (tomorrow)
  const getDefaultEta = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    // Use local date components instead of UTC to avoid timezone issues
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch possible stages when modal opens
  useEffect(() => {
    if (open) {
      fetchPossibleStages();
    }
  }, [open]);

  // Pre-select current location when modal opens and reset when it closes
  useEffect(() => {
    if (open && locations.length > 0) {
      // Use currentStage prop (from API) or fall back to vehicle.recon_stages
      const stageData = currentStage?.success ? currentStage :
        (vehicle.recon_stages && vehicle.recon_stages.length > 0 ? vehicle.recon_stages[0] : null);

      if (stageData) {
        // Find matching location - match on available fields (stage3 is most specific)
        const matchingLocation = locations.find((loc) => {
          // Match stage3 (most specific level) and stage2
          const stage3Match = loc.name === stageData.stage3;
          const stage2Match = loc.stage2 === stageData.stage2;

          // If both lot and stage exist in current, match them too
          const lotMatch = !stageData.stage || stageData.stage === 'undefined' || loc.stage === stageData.stage;

          return stage3Match && stage2Match && lotMatch;
        });

        if (matchingLocation) {
          setFormData({ locationNew: matchingLocation });
          setInitialLocationId(matchingLocation.id);
        }
      }

      // Prefill ETA from currentStage if available, otherwise default to tomorrow
      const prefillEta = currentStage?.eta || getDefaultEta();
      setEta(prefillEta);
      setInitialEta(currentStage?.eta || "");
    } else if (!open) {
      // Reset form when modal closes
      setFormData({ locationNew: null });
      setEta("");
      setInitialLocationId(null);
      setInitialEta("");
      setNoteText("");
      setSelectedFiles([]);
    }
  }, [open, locations, vehicle.recon_stages, currentStage]);

  const fetchPossibleStages = async () => {
    setLoadingLocations(true);
    try {
      const response = await apiService.get<StagesResponse>(RECON.possibleStages);
      if (response.success && response.stages) {
        // Extract unique stage3 values (locations)
        const stages = response.stages as ReconStage[];
        const uniqueStage3Values = Array.from(
          new Map(
            stages
              .filter((stage) => stage.stage3)
              .map((stage) => [
                stage.stage3,
                {
                  id: stage.id,
                  name: stage.stage3,
                  lot: stage.lot,
                  stage: stage.stage,
                  stage2: stage.stage2,
                } as LocationOption,
              ])
          ).values()
        );
        setLocations(uniqueStage3Values);
      }
    } catch (error) {
      console.error("Error fetching possible stages:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.locationNew) {
      toast.error("Please select a location.");
      return;
    }

    if (!user?.email) {
      toast.error("User email not found. Please log in again.");
      return;
    }

    // Detect what changed
    const locationChanged = formData.locationNew?.id !== initialLocationId;
    const etaChanged = eta !== initialEta;

    // If nothing changed, don't submit
    if (!locationChanged && !etaChanged) {
      toast.info("No changes detected.");
      return;
    }

    // Validate note when location is changing
    if (locationChanged && noteText.trim().length < 5) {
      return;
    }

    setSubmitting(true);

    try {
      let response;

      if (!locationChanged && etaChanged && currentStage?.request_id) {
        // PATCH: Only ETA changed and we have a request_id
        response = await apiService.patch<{ success: boolean; message?: string }>(
          RECON.updateVehicleStageEta(currentStage.request_id),
          { eta: eta || null, user_email: user?.email }
        );
      } else {
        // POST: Location changed (with or without ETA change)
        const payload = {
          vehicle_vin: vehicle.vin,
          stage: formData.locationNew.stage,
          stage2: formData.locationNew.stage2,
          stage3: formData.locationNew.name,
          user_email: user?.email,
          eta: eta || null,
        };

        response = await apiService.post<{ success: boolean; message?: string }>(
          RECON.changeReconStage(vehicle.vehicle_inventory_id || ""),
          payload
        );
      }

      if (response.success) {
        // Create note if location changed and note was provided
        if (locationChanged && noteText.trim() && vehicle.vehicle_inventory_id) {
          try {
            const noteResponse = await addNoteMutation.mutateAsync({
              vehicleInventoryId: vehicle.vehicle_inventory_id,
              note: noteText,
              userEmail: user.email,
            });

            const noteId = noteResponse.note?.note_id;
            if (selectedFiles.length > 0 && noteId) {
              uploadManager.queueUploads(
                selectedFiles,
                "note",
                noteId,
                user.email,
                () => {
                  queryClient.invalidateQueries({ queryKey: ["entity-attachments", "note", noteId] });
                }
              );
            }
          } catch (noteError) {
            console.error("Failed to create location change note:", noteError);
            // Note creation failure does not block the location change success
          }
        }

        toast.success(locationChanged ? "Location updated successfully!" : "ETA updated successfully!");
        onSuccess?.();
        onOpenChange(false);
        // Reset form
        setFormData({ locationNew: null });
      }
    } catch (error: unknown) {
      console.error("Error updating vehicle stage:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An unknown error occurred";
      toast.error(`Failed to update: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLocation = formData.locationNew;
  const locationChanged = formData.locationNew?.id !== initialLocationId;
  const noteValid = !locationChanged || noteText.trim().length >= 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Vehicle Location</DialogTitle>
          <DialogDescription>
            Update the recon location for VIN: {vehicle.vin}
          </DialogDescription>Location Repor
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="location">
              Recon Location <span className="text-destructive">*</span>
            </Label>

            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between"
                  disabled={loadingLocations}
                >
                  {loadingLocations ? (
                    <span className="flex items-center gap-2">
                      <Loader size={14} className="animate-spin" />
                      Loading locations...
                    </span>
                  ) : selectedLocation ? (
                    <span className="truncate">{selectedLocation.name}</span>
                  ) : (
                    "Select location..."
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[460px] p-0"
                align="start"
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <Command
                  filter={(value, search) => {
                    const valueLower = value.toLowerCase();
                    const searchLower = search.toLowerCase();
                    // Exact match gets highest score
                    if (valueLower === searchLower) return 1;
                    // Starts with search term gets high score
                    if (valueLower.startsWith(searchLower)) return 0.8;
                    // Contains search term gets medium score
                    if (valueLower.includes(searchLower)) return 0.6;
                    // No match
                    return 0;
                  }}
                >
                  <CommandInput placeholder="Search locations..." />
                  <CommandList className="max-h-[300px] overflow-y-auto">
                    <CommandEmpty>No location found.</CommandEmpty>
                    <CommandGroup>
                      {locations.map((location) => (
                        <CommandItem
                          key={location.id}
                          value={`${location.name} ${location.stage2}`}
                          onSelect={() => {
                            setFormData({
                              locationNew:
                                formData.locationNew?.id === location.id
                                  ? null
                                  : location,
                            });
                            setComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedLocation?.id === location.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{location.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {location.lot} › {location.stage} › {location.stage2}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* ETA Field */}
            <div className="space-y-2">
              <Label htmlFor="eta">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Expected Completion Date (Optional)</span>
                </div>
              </Label>
              <Input
                id="eta"
                type="date"
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                disabled={submitting}
              />
            </div>

            {/* Note Field — shown only when location is changing */}
            {locationChanged && (
              <div className="space-y-2">
                <Label htmlFor="location-note">
                  Note <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="location-note"
                  placeholder="Describe the reason for this location change..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                  className="resize-none"
                  disabled={submitting}
                />
                {noteText.trim().length > 0 && noteText.trim().length < 5 && (
                  <p className="text-xs text-destructive">Note must be at least 5 characters.</p>
                )}
              </div>
            )}

            {/* Attachments — shown only when location is changing */}
            {locationChanged && (
              <FilePickerZone
                files={selectedFiles}
                onChange={setSelectedFiles}
                inputId="location-change-file-input"
                disabled={submitting}
              />
            )}

            {/* Selected Location Info */}
            {selectedLocation && (
              <div className="mt-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-primary">
                  Selected: {selectedLocation.name}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Full Path: {selectedLocation.lot} › {selectedLocation.stage} › {selectedLocation.stage2} › {selectedLocation.name}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || addNoteMutation.isPending || !formData.locationNew || !noteValid}
            >
              {submitting ? (
                <>
                  <Loader size={16} className="mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Location"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
